import React, { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import {
	Card,
	Row,
	Col,
	Button,
	message,
	Typography,
	Space,
	Input,
	DatePicker,
	Select,
	Form,
	Table,
	Tag,
	Checkbox,
} from "antd";
import {
	ArrowLeftOutlined,
	UserOutlined,
	SaveOutlined,
	CheckOutlined,
} from "@ant-design/icons";
import useIsMobile from "../../hooks/useIsMobile";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const BulkEditMembers = () => {
	const isMobile = useIsMobile();
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [members, setMembers] = useState([]);
	const [selectedMembers, setSelectedMembers] = useState([]);
	const [searchText, setSearchText] = useState("");
	const [searchCategory, setSearchCategory] = useState("name");
	const [filteredMembers, setFilteredMembers] = useState([]);

	// Dropdown data
	const [classificationTypes, setClassificationTypes] = useState([]);
	const [statusTypes, setStatusTypes] = useState([]);
	const [baptismalStatusTypes, setBaptismalStatusTypes] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [districts, setDistricts] = useState([]);
	const [zones, setZones] = useState([]);
	const [cells, setCells] = useState([]);

	// Selected values for cascading selects
	const [selectedDistrict, setSelectedDistrict] = useState(null);
	const [selectedZone, setSelectedZone] = useState(null);

	// Checkbox states for which fields to update
	const [updateFields, setUpdateFields] = useState({
		address: false,
		classification: false,
		status: false,
		baptismal_status: false,
		affiliated_department: false,
		cell: false,
	});

	useEffect(() => {
		fetchMembers();
		fetchDropdownData();
	}, []);

	const fetchMembers = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				"/users/all-users/",
				{
					params: {
						include_inactive: "true",
					},
				}
			);
			if (response.status === 200) {
				setMembers(response.data);
			}
		} catch (error) {
			message.error("회원 목록을 불러오는데 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const fetchDropdownData = async () => {
		try {
			const [
				classificationsRes,
				statusesRes,
				baptismalStatusesRes,
				departmentsRes,
				districtsRes,
			] = await Promise.all([
				useAxios.get("/users/classifications/"),
				useAxios.get("/users/statuses/"),
				useAxios.get("/users/baptismal-statuses/"),
				useAxios.get("/department/departments/"),
				useAxios.get("/district/districts/"),
			]);

			if (classificationsRes.status === 200) {
				setClassificationTypes(classificationsRes.data);
			}
			if (statusesRes.status === 200) {
				setStatusTypes(statusesRes.data);
			}
			if (baptismalStatusesRes.status === 200) {
				setBaptismalStatusTypes(
					baptismalStatusesRes.data
				);
			}
			if (departmentsRes.status === 200) {
				setDepartments(departmentsRes.data);
			}
			if (districtsRes.status === 200) {
				setDistricts(districtsRes.data);
			}
		} catch (error) {
			message.error("데이터를 불러오는데 실패했습니다.");
		}
	};

	const filterMembers = useCallback(() => {
		if (!searchText) {
			setFilteredMembers(members);
			return;
		}

		const filtered = members.filter((member) => {
			let searchValue = "";
			switch (searchCategory) {
				case "name":
					searchValue = member.name || "";
					break;
				case "phone_number":
					searchValue = member.phone_number || "";
					break;
				case "affiliated_group":
					searchValue =
						member.affiliated_group || "";
					break;
				default:
					searchValue = member.name || "";
			}
			return searchValue
				.toLowerCase()
				.includes(searchText.toLowerCase());
		});

		setFilteredMembers(filtered);
	}, [members, searchText, searchCategory]);

	useEffect(() => {
		filterMembers();
	}, [filterMembers]);

	const hasMixedGenders = () => {
		const genders = new Set(
			selectedMembers.map((member) => member.gender)
		);
		return genders.size > 1;
	};

	const handleSearch = (value) => {
		setSearchText(value);
	};

	const handleCategoryChange = (value) => {
		setSearchCategory(value);
	};

	const handleMemberSelection = (selectedRowKeys, selectedRows) => {
		// Get all currently selected members (including those not in current filtered view)
		const allSelectedMembers = [...selectedMembers];

		// Update the selection for the current filtered members
		selectedRows.forEach((selectedRow) => {
			const existingIndex = allSelectedMembers.findIndex(
				(member) => member.id === selectedRow.id
			);
			if (existingIndex === -1) {
				// Add new selection
				allSelectedMembers.push(selectedRow);
			}
		});

		// Remove deselected members from current filtered view
		const currentFilteredIds = filteredMembers.map(
			(member) => member.id
		);
		const finalSelectedMembers = allSelectedMembers.filter(
			(member) => {
				// Keep if it's in the current filtered view and selected
				if (currentFilteredIds.includes(member.id)) {
					return selectedRowKeys.includes(
						member.id
					);
				}
				// Keep if it's not in current filtered view (preserve previous selections)
				return true;
			}
		);

		setSelectedMembers(finalSelectedMembers);
	};

	const handleRemoveMember = (memberId) => {
		setSelectedMembers(
			selectedMembers.filter(
				(member) => member.id !== memberId
			)
		);
	};

	const handleFieldCheckboxChange = (field, checked) => {
		setUpdateFields((prev) => ({
			...prev,
			[field]: checked,
		}));

		// Reset form fields when unchecked
		if (!checked) {
			if (field === "address") {
				form.setFieldsValue({
					address: undefined,
					address_detail: undefined,
				});
			} else if (field === "cell") {
				form.setFieldsValue({
					district: undefined,
					zone: undefined,
					cell_id: undefined,
					cell_change_date: undefined,
					cell_change_reason: undefined,
				});
				setSelectedDistrict(null);
				setSelectedZone(null);
				setZones([]);
				setCells([]);
			} else {
				form.setFieldsValue({
					[field]: undefined,
					[`${field}_change_date`]: undefined,
					[`${field}_change_reason`]: undefined,
				});
			}
		}
	};

	const handleDistrictChange = async (districtId) => {
		setSelectedDistrict(districtId);
		setSelectedZone(null);
		setZones([]);
		setCells([]);
		form.setFieldsValue({
			zone: undefined,
			cell_id: undefined,
		});

		// Load zones for all missionary types
		try {
			const [namsunRes, yeosunRes] = await Promise.all([
				useAxios.get(
					`/district/zones-by-district-missionary/${districtId}/남선/`
				),
				useAxios.get(
					`/district/zones-by-district-missionary/${districtId}/여선/`
				),
			]);

			const allZones = [
				...(namsunRes.status === 200
					? namsunRes.data
					: []),
				...(yeosunRes.status === 200
					? yeosunRes.data
					: []),
			];
			setZones(allZones);
		} catch (error) {
			message.error("지구 목록을 불러오는데 실패했습니다.");
		}
	};

	const handleZoneChange = async (zoneId) => {
		setSelectedZone(zoneId);
		setCells([]);
		form.setFieldsValue({ cell_id: undefined });

		if (zoneId) {
			try {
				const response = await useAxios.get(
					`/district/cells-by-zone/${zoneId}/`
				);
				if (response.status === 200) {
					setCells(response.data);
				}
			} catch (error) {
				message.error(
					"구역 목록을 불러오는데 실패했습니다."
				);
			}
		}
	};

	const handleSubmit = async () => {
		if (selectedMembers.length === 0) {
			message.error("최소 한 명의 회원을 선택해주세요.");
			return;
		}

		const hasAnyFieldSelected = Object.values(updateFields).some(
			(val) => val
		);
		if (!hasAnyFieldSelected) {
			message.error("최소 한 개의 수정 항목을 선택해주세요.");
			return;
		}

		try {
			setSaving(true);
			const values = await form.validateFields();

			const updateData = {
				user_ids: selectedMembers.map(
					(member) => member.id
				),
			};

			// Address (grouped together)
			if (updateFields.address) {
				if (
					values.address !== undefined &&
					values.address !== null
				) {
					updateData.address = values.address;
				}
				if (
					values.address_detail !== undefined &&
					values.address_detail !== null
				) {
					updateData.address_detail =
						values.address_detail;
				}
			}

			// Classification
			if (
				updateFields.classification &&
				values.classification_id
			) {
				updateData.classification_id =
					values.classification_id;
				if (values.classification_change_date) {
					updateData.classification_change = {
						changed_date:
							values.classification_change_date.format(
								"YYYY-MM-DD"
							),
						changed_classification:
							classificationTypes.find(
								(c) =>
									c.id ===
									values.classification_id
							)?.name || "",
						reason:
							values.classification_change_reason ||
							"",
					};
				}
			}

			// Status
			if (updateFields.status && values.status_id) {
				updateData.status_id = values.status_id;
				if (values.status_change_date) {
					updateData.status_change = {
						changed_date:
							values.status_change_date.format(
								"YYYY-MM-DD"
							),
						changed_status:
							statusTypes.find(
								(s) =>
									s.id ===
									values.status_id
							)?.name || "",
						reason:
							values.status_change_reason ||
							"",
					};
				}
			}

			// Baptismal status
			if (
				updateFields.baptismal_status &&
				values.baptismal_status
			) {
				updateData.baptismal_status =
					values.baptismal_status;
				if (values.baptismal_change_date) {
					updateData.baptismal_change = {
						baptismal_status_type:
							values.baptismal_status,
						baptismal_date:
							values.baptismal_change_date.format(
								"YYYY-MM-DD"
							),
						baptizing_pastor:
							values.baptizing_pastor ||
							"",
						baptismal_church:
							values.baptismal_church ||
							"",
					};
				}
			}

			// Affiliated department
			if (
				updateFields.affiliated_department &&
				values.affiliated_department_id
			) {
				updateData.affiliated_department_id =
					values.affiliated_department_id;
				if (values.department_change_date) {
					updateData.department_change = {
						changed_date:
							values.department_change_date.format(
								"YYYY-MM-DD"
							),
						changed_department:
							departments.find(
								(d) =>
									d.id ===
									values.affiliated_department_id
							)?.name || "",
						reason:
							values.department_change_reason ||
							"",
					};
				}
			}

			// Cell
			if (updateFields.cell && values.cell_id) {
				updateData.cell_id = values.cell_id;
				if (values.cell_change_date) {
					const selectedCell = cells.find(
						(c) => c.id === values.cell_id
					);
					const selectedZone = zones.find(
						(z) => z.id === values.zone
					);
					const selectedDistrictObj =
						districts.find(
							(d) =>
								d.id ===
								values.district
						);
					updateData.cell_change = {
						changed_date:
							values.cell_change_date.format(
								"YYYY-MM-DD"
							),
						changed_cell: `${
							selectedDistrictObj?.name ||
							""
						} ${selectedZone?.name || ""} ${
							selectedCell?.name || ""
						}`.trim(),
						reason:
							values.cell_change_reason ||
							"",
					};
				}
			}

			const response = await useAxios.post(
				"/users/bulk-update-members/",
				updateData
			);

			if (response.status === 200) {
				message.success(
					`${response.data.updated_count}명의 회원 정보가 성공적으로 수정되었습니다.`
				);
				if (response.data.error_count > 0) {
					message.warning(
						`${response.data.error_count}명의 회원 정보 수정 중 오류가 발생했습니다.`
					);
				}
				// Reset form and selections
				form.resetFields();
				setSelectedMembers([]);
				setUpdateFields({
					address: false,
					classification: false,
					status: false,
					baptismal_status: false,
					affiliated_department: false,
					cell: false,
				});
				setSelectedDistrict(null);
				setSelectedZone(null);
				setZones([]);
				setCells([]);
			}
		} catch (error) {
			message.error("회원 정보 수정에 실패했습니다.");
			console.error(error);
		} finally {
			setSaving(false);
		}
	};

	const memberColumns = [
		{
			title: "이름",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "연락처",
			dataIndex: "phone_number",
			key: "phone_number",
		},
		{
			title: "소속",
			dataIndex: "affiliated_group",
			key: "affiliated_group",
		},
		{
			title: "상태",
			dataIndex: "status_name",
			key: "status_name",
			render: (status) => (
				<Tag
					color={
						status === "입교인"
							? "blue"
							: "default"
					}>
					{status}
				</Tag>
			),
		},
	];

	const rowSelection = {
		type: "checkbox",
		selectedRowKeys: selectedMembers.map((member) => member.id),
		onChange: handleMemberSelection,
	};

	return (
		<>
			<PageTitle title="회원 일괄 수정" />
			<div style={{ padding: "24px" }}>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: 24,
					}}>
					<Button
						icon={<ArrowLeftOutlined />}
						onClick={() =>
							navigate(
								"../view-all-members"
							)
						}>
						목록으로
					</Button>
					<Title level={2}>회원 일괄 수정</Title>
					<Button
						type="primary"
						icon={<SaveOutlined />}
						loading={saving}
						onClick={handleSubmit}>
						저장
					</Button>
				</div>

				<Form form={form} layout="vertical">
					{/* Member Selection Section */}
					<Card
						title={
							<Space>
								<UserOutlined />
								회원 선택
							</Space>
						}
						style={{ marginBottom: 24 }}>
						<Row
							gutter={[16, 16]}
							style={{
								marginBottom: 16,
							}}>
							<Col
								xs={24}
								sm={12}
								md={6}>
								<Select
									defaultValue="name"
									style={{
										width: "100%",
									}}
									onChange={
										handleCategoryChange
									}
									options={[
										{
											value: "name",
											label: "이름",
										},
										{
											value: "phone_number",
											label: "연락처",
										},
										{
											value: "affiliated_group",
											label: "소속",
										},
									]}
								/>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={18}>
								<Input.Search
									placeholder="검색어를 입력하세요"
									allowClear
									onSearch={
										handleSearch
									}
									style={{
										width: "100%",
									}}
								/>
							</Col>
						</Row>

						<Table
							columns={memberColumns}
							dataSource={
								filteredMembers
							}
							rowKey="id"
							loading={loading}
							rowSelection={
								rowSelection
							}
							pagination={{
								pageSize: 10,
								showSizeChanger: true,
								showQuickJumper: true,
							}}
							scroll={{ y: 300 }}
						/>

						{selectedMembers.length > 0 && (
							<div
								style={{
									marginTop: 16,
								}}>
								<Text strong>
									선택된
									회원:{" "}
									{
										selectedMembers.length
									}
									명
								</Text>
								<div
									style={{
										marginTop: 8,
									}}>
									{selectedMembers.map(
										(
											member,
											index
										) => (
											<Tag
												key={
													member.id
												}
												closable
												onClose={() =>
													handleRemoveMember(
														member.id
													)
												}
												style={{
													marginBottom: 4,
													marginRight: 4,
												}}>
												{
													member.name
												}
											</Tag>
										)
									)}
								</div>
							</div>
						)}
					</Card>

					{/* Update Fields Section */}
					<Card
						title={
							<Space>
								<CheckOutlined />
								수정 항목 선택
							</Space>
						}
						style={{ marginBottom: 24 }}>
						<Row gutter={[24, 16]}>
							{/* Address */}
							<Col
								xs={24}
								sm={12}
								md={8}>
								<Checkbox
									checked={
										updateFields.address
									}
									onChange={(
										e
									) =>
										handleFieldCheckboxChange(
											"address",
											e
												.target
												.checked
										)
									}>
									주소
								</Checkbox>
								{updateFields.address && (
									<div
										style={{
											marginTop: 8,
										}}>
										<Form.Item
											name="address"
											label="주소"
											rules={[
												{
													required: true,
													message: "주소를 입력해주세요",
												},
											]}>
											<Input placeholder="주소 입력" />
										</Form.Item>
										<Form.Item
											name="address_detail"
											label="상세주소">
											<Input placeholder="상세주소 입력" />
										</Form.Item>
									</div>
								)}
							</Col>

							{/* Classification */}
							<Col
								xs={24}
								sm={12}
								md={8}>
								<Checkbox
									checked={
										updateFields.classification
									}
									onChange={(
										e
									) =>
										handleFieldCheckboxChange(
											"classification",
											e
												.target
												.checked
										)
									}>
									분류
								</Checkbox>
								{updateFields.classification && (
									<div
										style={{
											marginTop: 8,
										}}>
										<Form.Item
											name="classification_id"
											label="분류"
											rules={[
												{
													required: true,
													message: "분류를 선택해주세요",
												},
											]}>
											<Select placeholder="분류 선택">
												{classificationTypes.map(
													(
														type
													) => (
														<Option
															key={
																type.id
															}
															value={
																type.id
															}>
															{
																type.name
															}
														</Option>
													)
												)}
											</Select>
										</Form.Item>
										<Form.Item
											name="classification_change_date"
											label="변경일"
											rules={[
												{
													required: true,
													message: "변경일을 선택해주세요",
												},
											]}>
											<DatePicker
												inputReadOnly={isMobile}
												style={{
													width: "100%",
												}}
												format="YYYY-MM-DD"
												placeholder="변경일 선택"
											/>
										</Form.Item>
										<Form.Item
											name="classification_change_reason"
											label="변경 사유"
											rules={[
												{
													required: true,
													message: "변경 사유를 입력해주세요",
												},
											]}>
											<TextArea
												rows={
													2
												}
												placeholder="변경 사유 입력"
											/>
										</Form.Item>
									</div>
								)}
							</Col>

							{/* Status */}
							<Col
								xs={24}
								sm={12}
								md={8}>
								<Checkbox
									checked={
										updateFields.status
									}
									onChange={(
										e
									) =>
										handleFieldCheckboxChange(
											"status",
											e
												.target
												.checked
										)
									}>
									상태
								</Checkbox>
								{updateFields.status && (
									<div
										style={{
											marginTop: 8,
										}}>
										<Form.Item
											name="status_id"
											label="상태"
											rules={[
												{
													required: true,
													message: "상태를 선택해주세요",
												},
											]}>
											<Select placeholder="상태 선택">
												{statusTypes.map(
													(
														type
													) => (
														<Option
															key={
																type.id
															}
															value={
																type.id
															}>
															{
																type.name
															}
														</Option>
													)
												)}
											</Select>
										</Form.Item>
										<Form.Item
											name="status_change_date"
											label="변경일"
											rules={[
												{
													required: true,
													message: "변경일을 선택해주세요",
												},
											]}>
											<DatePicker
												inputReadOnly={isMobile}
												style={{
													width: "100%",
												}}
												format="YYYY-MM-DD"
												placeholder="변경일 선택"
											/>
										</Form.Item>
										<Form.Item
											name="status_change_reason"
											label="변경 사유"
											rules={[
												{
													required: true,
													message: "변경 사유를 입력해주세요",
												},
											]}>
											<TextArea
												rows={
													2
												}
												placeholder="변경 사유 입력"
											/>
										</Form.Item>
									</div>
								)}
							</Col>

							{/* Baptismal Status */}
							<Col
								xs={24}
								sm={12}
								md={8}>
								<Checkbox
									checked={
										updateFields.baptismal_status
									}
									onChange={(
										e
									) =>
										handleFieldCheckboxChange(
											"baptismal_status",
											e
												.target
												.checked
										)
									}>
									세례상태
								</Checkbox>
								{updateFields.baptismal_status && (
									<div
										style={{
											marginTop: 8,
										}}>
										<Form.Item
											name="baptismal_status"
											label="세례상태"
											rules={[
												{
													required: true,
													message: "세례상태를 선택해주세요",
												},
											]}>
											<Select placeholder="세례상태 선택">
												{baptismalStatusTypes.map(
													(
														type
													) => (
														<Option
															key={
																type.id
															}
															value={
																type.name
															}>
															{
																type.name
															}
														</Option>
													)
												)}
											</Select>
										</Form.Item>
										<Form.Item
											name="baptismal_change_date"
											label="집례일"
											rules={[
												{
													required: true,
													message: "집례일을 선택해주세요",
												},
											]}>
											<DatePicker
												inputReadOnly={isMobile}
												style={{
													width: "100%",
												}}
												format="YYYY-MM-DD"
												placeholder="집례일 선택"
											/>
										</Form.Item>
										<Form.Item
											name="baptizing_pastor"
											label="집례 목사">
											<Input placeholder="집례 목사 입력" />
										</Form.Item>
										<Form.Item
											name="baptismal_church"
											label="집례 교회">
											<Input placeholder="집례 교회 입력" />
										</Form.Item>
									</div>
								)}
							</Col>

							{/* Affiliated Department */}
							<Col
								xs={24}
								sm={12}
								md={8}>
								<Checkbox
									checked={
										updateFields.affiliated_department
									}
									onChange={(
										e
									) =>
										handleFieldCheckboxChange(
											"affiliated_department",
											e
												.target
												.checked
										)
									}>
									소속기관
								</Checkbox>
								{updateFields.affiliated_department && (
									<div
										style={{
											marginTop: 8,
										}}>
										<Form.Item
											name="affiliated_department_id"
											label="소속기관"
											rules={[
												{
													required: true,
													message: "소속기관을 선택해주세요",
												},
											]}>
											<Select placeholder="소속기관 선택">
												{departments.map(
													(
														dept
													) => (
														<Option
															key={
																dept.id
															}
															value={
																dept.id
															}>
															{
																dept.name
															}
														</Option>
													)
												)}
											</Select>
										</Form.Item>
										<Form.Item
											name="department_change_date"
											label="변경일"
											rules={[
												{
													required: true,
													message: "변경일을 선택해주세요",
												},
											]}>
											<DatePicker
												inputReadOnly={isMobile}
												style={{
													width: "100%",
												}}
												format="YYYY-MM-DD"
												placeholder="변경일 선택"
											/>
										</Form.Item>
										<Form.Item
											name="department_change_reason"
											label="변경 사유"
											rules={[
												{
													required: true,
													message: "변경 사유를 입력해주세요",
												},
											]}>
											<TextArea
												rows={
													2
												}
												placeholder="변경 사유 입력"
											/>
										</Form.Item>
									</div>
								)}
							</Col>

							{/* Cell */}
							<Col
								xs={24}
								sm={12}
								md={8}>
								<Checkbox
									checked={
										updateFields.cell
									}
									onChange={(
										e
									) =>
										handleFieldCheckboxChange(
											"cell",
											e
												.target
												.checked
										)
									}>
									소속
									구역
								</Checkbox>
								{updateFields.cell && (
									<div
										style={{
											marginTop: 8,
										}}>
										{hasMixedGenders() && (
											<div
												style={{
													marginBottom: 8,
													padding: 8,
													backgroundColor:
														"#fff7e6",
													border: "1px solid #ffd591",
													borderRadius: 4,
												}}>
												<Text type="warning">
													선택된
													회원
													중
													남자와
													여자가
													섞여있어
													교구를
													일괄
													변경할
													수
													없습니다.
													같은
													성별의
													회원만
													선택해주세요.
												</Text>
											</div>
										)}
										<Form.Item
											name="district"
											label="교구"
											rules={[
												{
													required: true,
													message: "교구를 선택해주세요",
												},
											]}>
											<Select
												placeholder="교구 선택"
												disabled={hasMixedGenders()}
												onChange={
													handleDistrictChange
												}>
												{districts.map(
													(
														district
													) => (
														<Option
															key={
																district.id
															}
															value={
																district.id
															}>
															{
																district.name
															}

															대
															교구
														</Option>
													)
												)}
											</Select>
										</Form.Item>
										<Form.Item
											name="zone"
											label="지구"
											rules={[
												{
													required: true,
													message: "지구를 선택해주세요",
												},
											]}>
											<Select
												placeholder="지구 선택"
												disabled={
													!selectedDistrict ||
													hasMixedGenders()
												}
												onChange={
													handleZoneChange
												}>
												{zones.map(
													(
														zone
													) => (
														<Option
															key={
																zone.id
															}
															value={
																zone.id
															}>
															{
																zone.name
															}
															지구
														</Option>
													)
												)}
											</Select>
										</Form.Item>
										<Form.Item
											name="cell_id"
											label="구역"
											rules={[
												{
													required: true,
													message: "구역을 선택해주세요",
												},
											]}>
											<Select
												placeholder="구역 선택"
												disabled={
													!selectedZone ||
													hasMixedGenders()
												}>
												{cells.map(
													(
														cell
													) => (
														<Option
															key={
																cell.id
															}
															value={
																cell.id
															}>
															{
																cell.name
															}
															구역
														</Option>
													)
												)}
											</Select>
										</Form.Item>
										<Form.Item
											name="cell_change_date"
											label="변경일"
											rules={[
												{
													required: true,
													message: "변경일을 선택해주세요",
												},
											]}>
											<DatePicker
												inputReadOnly={isMobile}
												style={{
													width: "100%",
												}}
												format="YYYY-MM-DD"
												placeholder="변경일 선택"
											/>
										</Form.Item>
										<Form.Item
											name="cell_change_reason"
											label="변경 사유"
											rules={[
												{
													required: true,
													message: "변경 사유를 입력해주세요",
												},
											]}>
											<TextArea
												rows={
													2
												}
												placeholder="변경 사유 입력"
											/>
										</Form.Item>
									</div>
								)}
							</Col>
						</Row>
					</Card>
				</Form>
			</div>
		</>
	);
};

export default BulkEditMembers;
